import { Component, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { NiceSelectComponent } from 'src/app/components/nice-select/nice-select.component';
import { ProductServiceSelectComponent } from 'src/app/components/product-service-select/product-service-select.component';
import { PropertyOfficeSelectComponent } from 'src/app/components/property-office-select/property-office-select.component';
import { SelectComponent } from 'src/app/components/select/select.component';
import { CaseDocument, CaseStatus, CaseType } from 'src/app/models/case';
import { NiceClass } from 'src/app/models/niceClass';
import { Paginator } from 'src/app/models/paginator';
import { ProductService } from 'src/app/models/productService';
import { Trademark } from 'src/app/models/trademark';
import { CaseCreationOrUpdate, CaseService } from 'src/app/services/case.service';
import { MessageService } from 'src/app/services/message.service';
import { PropertyOfficeService } from 'src/app/services/property-office.service';
import { TrademarkService } from 'src/app/services/trademark.service';

enum CaseStepEnum {
  CASE_TYPE = 1,
  TRADEMARK,
  NICE_CLASS,
  OFFICE,
  PERIOD,
  LANGUAGE,
  REFERENCE
}

const RENEWAL_MAINTENANCE_TERRITORIES = ['US', 'US-PR', 'MX', 'PH', 'KH', 'DZ', 'SZ', 'CA', 'AR', 'BR', 'VE', 'CO', 'EC', 'PE', 'BO', 'PY', 'CL', 'UY', 'SR', 'GY'];

@Component({
  selector: 'app-case-declare',
  templateUrl: './case-declare.component.html',
  styleUrls: ['./case-declare.component.scss']
})
export class CaseDeclareComponent {

  public CaseStepEnum = CaseStepEnum;

  @ViewChild('caseTypeSelect') caseTypeSelect?: SelectComponent;
  @ViewChild('niceSelect') niceSelect?: NiceSelectComponent;
  @ViewChild('productServiceSelect') productServiceSelect?: ProductServiceSelectComponent;
  @ViewChild('propertyOfficeSelect') propertyOfficeSelect?: PropertyOfficeSelectComponent;

  trademark?: {
    ref: string;
    name: string;
    countryOfDesignation: string;
    identifierNumber: string;
    niceClassification: number[];
    registrationDate?: moment.Moment;
    applicationDate?: moment.Moment;
  };
  _trademark?: Trademark;
  obligationOfUse: {
    key: string,
    value: number,
    unit: string
  } = {
      key: 'registrationDate',
      value: 5,
      unit: 'years'
    };
  type?: CaseType;
  niceClassification: number[] = [];
  productsServices: ProductService[] = [];
  propertyOffice?: string;
  period: {
    start?: moment.Moment;
    end?: moment.Moment;
  } = {
      start: undefined,
      end: undefined,
    };
  lang?: string;

  documents: Paginator<CaseDocument> = {
    docs: [],
    page: 1,
    offset: 0,
    limit: 10,
    totalDocs: 0,
    sort: '-createdAt'
  };
  filteredDocuments: CaseDocument[] = [];

  linkedDocuments: CaseDocument[] = [];

  identifier?: string;

  loading: boolean = false;

  today: moment.Moment = moment();
  year: number = moment().year();

  caseTypes: {
    value: string,
    label: string,
    selected: boolean,
    disabled: boolean
  }[] = [
      {
        value: CaseType.PRINCIPAL,
        label: "CASE.TYPE_PRINCIPAL",
        selected: false,
        disabled: false
      },
      {
        value: CaseType.COUNTERCLAIM,
        label: "CASE.TYPE_COUNTERCLAIM",
        selected: false,
        disabled: false
      },
      {
        value: CaseType.RENEWAL_MAINTENANCE,
        label: "CASE.TYPE_RENEWAL_MAINTENANCE",
        selected: false,
        disabled: false
      }
    ];


  langs: {
    value: string,
    label: string,
    selected: boolean
  }[] = [
      {
        value: "cn",
        label: "LANGUAGES.cn",
        selected: false
      },
      {
        value: "en",
        label: "LANGUAGES.en",
        selected: false
      },
      {
        value: "fr",
        label: "LANGUAGES.fr",
        selected: false
      },
      {
        value: "de",
        label: "LANGUAGES.de",
        selected: false
      },
      {
        value: "it",
        label: "LANGUAGES.it",
        selected: false
      },
      {
        value: "es",
        label: "LANGUAGES.es",
        selected: false
      }
    ]
  endInstructionDateHint: boolean = false;

  constructor(
    private _message: MessageService,
    private caseService: CaseService,
    private router: Router,
    private toastr: ToastrService,
    private trademarkService: TrademarkService,
    private propertyOfficeService: PropertyOfficeService,
    private translate: TranslateService
  ) {

  }

  isStepDisabled(step: number) {
    switch (step) {
      case 1:
        return false;
      case 2:
        return !this.type;
      case 3:
        return !this.type || !this.trademark;
      case 4:
        return !this.type || !this.trademark || this.niceClassification.length === 0;
      case 5:
        return !this.type || !this.trademark || this.niceClassification.length === 0 || !this.propertyOffice;
      case 6:
        return !this.type || !this.trademark || this.niceClassification.length === 0 || !this.propertyOffice || !this.period || !this.period.start || !this.period.end;
      case 7:
        return !this.type || !this.trademark || this.niceClassification.length === 0 || !this.propertyOffice || !this.period || !this.period.start || !this.period.end || !this.lang;
      default:
        return true;
    }
  }

  async onTrademarkChange(event: { value?: Trademark }) {
    if (event && event.value) {
      this._trademark = event.value;
      this.trademark = {
        ref: event.value._id!,
        name: event.value.name!,
        identifierNumber: event.value.identifierNumber,
        countryOfDesignation: event.value.countryOfDesignation,
        niceClassification: event.value.niceClassification?.map(niceClass => niceClass.classNumber) ?? [],
        registrationDate: event.value.registrationDate,
        applicationDate: event.value.applicationDate
      };
      this.obligationOfUse = await this.trademarkService.retrieveObligationOfUse(this.trademark.ref);
    } else {
      this.trademark = undefined;
      this._trademark = undefined;
      this.niceClassification = [];
      this.niceSelect?.onClear();
      this.productsServices = [];
      this.productServiceSelect?.onClear();
      this.obligationOfUse = {
        key: 'registrationDate',
        value: 5,
        unit: 'years'
      }
    }
    if (this.trademark) {
      this.caseTypes.find(c => c.value === CaseType.RENEWAL_MAINTENANCE)!.disabled = !RENEWAL_MAINTENANCE_TERRITORIES.includes(this.trademark?.countryOfDesignation);
      if (!RENEWAL_MAINTENANCE_TERRITORIES.includes(this.trademark?.countryOfDesignation) && this.type === CaseType.RENEWAL_MAINTENANCE) {
        this.type = CaseType.PRINCIPAL;
        this.caseTypeSelect?.select(this.caseTypes.find(c => c.value === CaseType.PRINCIPAL)!);
        this.toastr.info('CASE_DECLARE.TERRITORY_NOT_SUPPORTED_FOR_RENEWAL')
      }
    }

  }

  onNiceClassificationChange(event: any) {
    this.niceClassification = event.value.map((niceClass: NiceClass) => niceClass.classNumber);
  }

  onProductServiceChange(event: any) {
    if (event?.value) {
      this.productsServices = event.value;
    }
  }

  _mapTmStartDate() {
    switch (this.obligationOfUse.key) {
      case 'registrationDate':
        return moment(this.trademark?.registrationDate) || moment();
      case 'applicationDate':
        return moment(this.trademark?.applicationDate) || moment()
      default:
        return moment();
    }
  }

  onCaseTypeChange(event: any) {
    if (event?.value) {
      this.type = event.value.value;
      this.propertyOfficeSelect?.retrievePropertyOffices();

      // If RENEWAL_MAINTENANCE then office = US-PR
      // if (this.type === CaseType.RENEWAL_MAINTENANCE) {
      //   this.propertyOfficeService.retrieve()
      //     .then(offices => {
      //       const renewal_offices = offices.filter(o => ['US', 'MX'].includes(o.code));
      //       console.log(renewal_offices)
      //       if (renewal_offices && renewal_offices.length > 0 && this.propertyOfficeSelect) {
      //         this.propertyOfficeSelect.propertyOffices = renewal_offices.map(office => ({
      //           ...office,
      //           selected: false,
      //           disabled: false
      //         }));
      //         console.log(this.propertyOfficeSelect.propertyOffices)
      //         // if (us_option) this.propertyOfficeSelect?.select(us_option);
      //       }
      //     })
      // }
    }
  }

  protected onPeriodStartChange(event: moment.Moment) {
    this.period.start = event;
  }

  protected onPeriodEndChange(event: moment.Moment) {
    this.period.end = event;
  }

  onPropertyOfficeChange(event: any) {
    if (event && event.value) {
      this.propertyOffice = event.value._id;
    }
  }

  onLangChange(event: any) {
    if (event && event.value) {
      this.lang = event.value.value;
    }
  }

  async declare() {
    try {
      this._message.emitChange("LOADING", "START");
      this.loading = true;

      if (this.trademark && this.niceClassification.length > 0 && this.type && this.propertyOffice && this.lang && this.period && this.period.start && this.period.end) {
        const caseToCreate: CaseCreationOrUpdate = {
          trademark: this.trademark.ref,
          type: this.type,
          niceClassification: this.niceClassification,
          productsServices: this.productsServices.map((ps) => ({
            ref: ps._id!,
            label: ps.label,
            classNumber: ps.classNumber,
            trademark: ps.trademark!.ref
          })),
          propertyOffice: this.propertyOffice,
          timePeriod: {
            start: this.period.start.format("YYYY-MM-DD"),
            end: this.period.end.format("YYYY-MM-DD"),
          },
          identifier: this.identifier,
          lang: this.lang,
          state: 'OPENED'
        }
        const caseCreated = await this.caseService.create(caseToCreate);
        this.toastr.success(this.translate.instant(this.identifier ? 'CASE_DECLARE.CASE_CREATED_IDENTIFIER_MESSAGE' : 'CASE_DECLARE.CASE_CREATED_MESSAGE', { identifier: this.identifier }), this.translate.instant('CASE_DECLARE.CASE_CREATED'), { tapToDismiss: true, disableTimeOut: true })
        this.router.navigateByUrl(`/cases/${caseCreated._id}`)
      }


      this.loading = false;
      this._message.emitChange("LOADING", "END");

    } catch (err: any) {
      this.loading = false;
      this._message.emitChange("LOADING", "END");
      if (err && err.status === 403 && err.error.message === "MAX_CASES_EXCEEDED") {
        this.toastr.error(`ORGANIZATION.MAX_CASES_EXCEEDED`);
      } else {
        this.toastr.error(`ERRORS.GENERIC`);
      }

    }
  }


  durationWarning() {
    return this.period && this.period.end && this.period.start && moment(this.period.end).isAfter(moment(this.period.start).add(this.obligationOfUse.value as moment.DurationInputArg1, this.obligationOfUse.unit as moment.DurationInputArg2));
  }

  _periodMessage() {
    return this.translate.instant(`CASE_DECLARE.SELECT_PERIOD_${this.type}`, {
      value: this.obligationOfUse.value,
      unit: this.translate.instant(`DATES.${this.obligationOfUse.unit.toUpperCase()}`).toLowerCase()
    })
  }

  _exceedMessage() {
    return this.translate.instant(`CASE_DECLARE.SELECT_PERIOD_EXCEEDED`, {
      value: this.obligationOfUse.value,
      unit: this.translate.instant(`DATES.${this.obligationOfUse.unit.toUpperCase()}`).toLowerCase()
    })
  }

}
